import { EPSILON, gt, gte, lt, lte, quantize, rangesOverlap } from '../time.js';
import { CommandError } from './types.js';
export function cloneItem(item) {
    return {
        ...item,
        source: item.source ? { ...item.source } : undefined,
        transform: { ...item.transform },
        crop: { ...item.crop },
        effects: item.effects.map((e) => ({ ...e, params: { ...e.params } })),
        keyframes: item.keyframes.map((k) => ({ ...k })),
        text: item.text ? { ...item.text } : undefined,
        caption: item.caption
            ? { ...item.caption, words: item.caption.words.map((w) => ({ ...w })) }
            : undefined,
        meta: { ...item.meta },
    };
}
export function cloneTrack(track) {
    return { ...track, items: track.items.map(cloneItem) };
}
export function itemEnd(item) {
    return item.start + item.duration;
}
/** Duração mínima de um item: um frame. Abaixo disso, o item deixa de existir. */
export function minDuration(fps) {
    return fps.den / fps.num;
}
/**
 * Desloca um item no tempo. As palavras de legenda são absolutas na timeline, então
 * acompanham o deslocamento; os keyframes são relativos ao item, então não se movem.
 */
export function shiftItem(item, delta, fps) {
    if (Math.abs(delta) < EPSILON)
        return item;
    const next = cloneItem(item);
    next.start = quantize(Math.max(0, item.start + delta), fps);
    if (next.caption) {
        next.caption.words = next.caption.words.map((w) => ({
            ...w,
            start: w.start + delta,
            end: w.end + delta,
        }));
    }
    return next;
}
/**
 * Recorta o item para cobrir exatamente [newStart, newEnd) da timeline.
 *
 * Resolve trim, split e carve com uma única primitiva — o que elimina a divergência
 * clássica entre "cortar pela esquerda" e "cortar pela direita" tratados em código
 * separado.
 */
export function sliceItem(item, newStart, newEnd, fps, newId) {
    const start = quantize(Math.max(newStart, item.start), fps);
    const end = quantize(Math.min(newEnd, itemEnd(item)), fps);
    const duration = end - start;
    if (duration < minDuration(fps) - EPSILON)
        return null;
    const next = cloneItem(item);
    if (newId)
        next.id = newId;
    const offset = start - item.start;
    next.start = start;
    next.duration = duration;
    const src = item.source;
    if (src) {
        const speed = Math.abs(item.speed) || 1;
        if (item.reverse) {
            const outAtStart = src.out - offset * speed;
            next.source = { assetId: src.assetId, in: outAtStart - duration * speed, out: outAtStart };
        }
        else {
            const inAtStart = src.in + offset * speed;
            next.source = { assetId: src.assetId, in: inAtStart, out: inAtStart + duration * speed };
        }
    }
    // Keyframes são relativos ao início do item: reancorar ao novo início.
    if (offset !== 0 && next.keyframes.length > 0) {
        next.keyframes = next.keyframes.map((k) => ({ ...k, time: k.time - offset }));
    }
    // Fades pertencem às bordas: recortar pela esquerda descarta o fade in, e vice-versa.
    if (offset > EPSILON)
        next.fadeIn = 0;
    if (end < itemEnd(item) - EPSILON)
        next.fadeOut = 0;
    next.fadeIn = Math.min(next.fadeIn, duration);
    next.fadeOut = Math.min(next.fadeOut, duration);
    if (next.caption) {
        next.caption.words = next.caption.words.filter((w) => rangesOverlap(w.start, w.end, start, end));
    }
    return next;
}
/**
 * Remove [start, end) da track deixando buraco. Um item que contém o range inteiro
 * é dividido em dois.
 */
export function carveRange(track, start, end, fps, idFactory) {
    if (lte(end, start))
        return track;
    const items = [];
    for (const item of track.items) {
        const iStart = item.start;
        const iEnd = itemEnd(item);
        if (!rangesOverlap(iStart, iEnd, start, end)) {
            items.push(item);
            continue;
        }
        // Sobra à esquerda.
        if (lt(iStart, start)) {
            const left = sliceItem(item, iStart, start, fps);
            if (left)
                items.push(left);
        }
        // Sobra à direita.
        if (gt(iEnd, end)) {
            const needsNewId = lt(iStart, start);
            const right = sliceItem(item, end, iEnd, fps, needsNewId ? idFactory() : undefined);
            if (right)
                items.push(right);
        }
    }
    return { ...track, items: sortItems(items) };
}
/** Desloca todos os itens que começam em/depois de `time`. */
export function shiftItemsAfter(track, time, delta, fps) {
    if (Math.abs(delta) < EPSILON)
        return track;
    const items = track.items.map((item) => gte(item.start, time - EPSILON) ? shiftItem(item, delta, fps) : item);
    return { ...track, items: sortItems(items) };
}
export function sortItems(items) {
    return [...items].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id));
}
/**
 * Insere um item sobrescrevendo o que estiver embaixo. É o comportamento padrão de
 * editores profissionais ao arrastar um clip para cima de outro.
 */
export function insertOverwrite(track, item, fps, idFactory) {
    const carved = carveRange(track, item.start, itemEnd(item), fps, idFactory);
    return { ...carved, items: sortItems([...carved.items, item]) };
}
/** Insere empurrando para a direita tudo que estiver a partir de `item.start`. */
export function insertRipple(track, item, fps) {
    const shifted = shiftItemsAfter(track, item.start, item.duration, fps);
    return { ...shifted, items: sortItems([...shifted.items, item]) };
}
/** Quantiza e sanitiza uma track. Última linha de defesa contra drift. */
export function normalizeTrack(track, fps) {
    const min = minDuration(fps);
    const items = sortItems(track.items
        .map((item) => {
        const start = quantize(Math.max(0, item.start), fps);
        const duration = quantize(item.duration, fps);
        if (start === item.start && duration === item.duration)
            return item;
        return { ...item, start, duration };
    })
        .filter((item) => item.duration >= min - EPSILON));
    return { ...track, items };
}
export function replaceTracks(seq, updated) {
    const byId = new Map(updated.map((t) => [t.id, t]));
    return {
        ...seq,
        tracks: seq.tracks.map((t) => byId.get(t.id) ?? t),
    };
}
export function snapshotTracks(seq, trackIds) {
    const wanted = new Set(trackIds);
    return seq.tracks.filter((t) => wanted.has(t.id)).map(cloneTrack);
}
export function requireTrack(seq, trackId) {
    const track = seq.tracks.find((t) => t.id === trackId);
    if (!track)
        throw new CommandError('TRACK_NOT_FOUND', `Track ${trackId} não existe`);
    return track;
}
export function requireItem(seq, itemId) {
    for (const track of seq.tracks) {
        const item = track.items.find((i) => i.id === itemId);
        if (item)
            return { track, item };
    }
    throw new CommandError('ITEM_NOT_FOUND', `Item ${itemId} não existe`);
}
/** Uma track travada rejeita qualquer escrita — inclusive vinda da IA. */
export function assertEditable(track) {
    if (track.locked) {
        throw new CommandError('TRACK_LOCKED', `Track ${track.name} está travada`);
    }
}
/** Tracks alvo de uma operação de range: as informadas, ou todas as destravadas. */
export function targetTracks(seq, trackIds) {
    if (trackIds && trackIds.length > 0) {
        return trackIds.map((id) => requireTrack(seq, id)).filter((t) => !t.locked);
    }
    return seq.tracks.filter((t) => !t.locked);
}
//# sourceMappingURL=helpers.js.map